import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { debounceTime, filter, switchMap, take, tap, map, distinctUntilChanged } from 'rxjs/operators';
import { ICON_TYPE, LaunchDialogService } from "@spartacus/storefront";
import { ActiveCartFacade, MultiCartFacade } from '@spartacus/cart/base/root';
import { Product, User } from '@spartacus/core';
import { ActivatedRoute, Router } from '@angular/router';
import { favoritesListSortConfig } from '../../../configs/favorites-list-sort.config';
import { GenGaListNames } from 'src/app/generac/enums/ga-list-names.enum';
import { GaItem, GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { GenLoadingEnum } from 'src/app/generac/enums/loading.enum';
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GenCustomerService } from 'src/app/generac/services/customer.service';
import { GenFavoritesService } from 'src/app/generac/services/favorites.service';
import { GenFavoritesResponse, GenFavoritesListPaginationMapped, GenFavoritesListPagination, GenFavoritesListItem, GenFavoritesListSortConfig } from 'src/app/generac/models/favorites.model';
import { GenFavotitesDialogActionsEnum } from 'src/app/generac/enums/favorites-dialogs-actions.enum';
import { GEN_FAVORITES_DIALOGS } from 'src/app/generac/enums/favorites-dialogs.enum';

@Component({
  selector: 'gen-favorites-list',
  templateUrl: './favorites-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class GenFavoritesListComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  favoritesList$: Observable<GenFavoritesResponse>;
  currentListId: string;
  tooltipMessage: string;
  phoneOrEmail: string;
  currentPage: number = 0;
  productsCount: number;
  favoritesList: Product[];
  pagination: GenFavoritesListPaginationMapped | GenFavoritesListPagination;
  loading: GenLoadingEnum = GenLoadingEnum.Idle;
  GenLoadingEnum = GenLoadingEnum;
  iconTypes = ICON_TYPE;
  userId: string;
  currentList: GenFavoritesListItem;
  sorts: GenFavoritesListSortConfig[];
  gaListName: GenGaListNames = GenGaListNames.FAVORITES;
  gaItems: GaItem[] = [];

  constructor(
    private genFavoritesService: GenFavoritesService,
    private userAccount: UserAccountFacade,
    protected customerService: GenCustomerService,
    protected cd: ChangeDetectorRef,
    protected launchDialogService: LaunchDialogService,
    protected multiCartFacade: MultiCartFacade,
    protected activeCartFacade: ActiveCartFacade,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    private baseStoreService: GenBaseStoreService,
  ) { }

  ngOnInit(): void {
    this.loading = GenLoadingEnum.Loading;
    this.setSortOptions();
    if (this.activatedRoute.snapshot?.queryParams.sort) {
      const sortToBeSelected = this.sorts.find(sort => sort.code == this.activatedRoute.snapshot?.queryParams.sort);
      sortToBeSelected.selected = true;
    } else {
      this.sorts[0].selected = true;
    }
    this.subscribeToLoadingState();
    this.subscribeToContactInfoAndTooltip();
    this.subscribeToActiveListChange();
  }

  private getFavorites(page: number = 0, sort: string, listId?: string): Observable<GenFavoritesResponse> {
    return this.userAccount.get().pipe(
      filter(user => !!user?.uid),
      take(1),
      tap((user: User) => this.userId = user?.uid),
      switchMap((user: User) => this.genFavoritesService.getFavorites(user?.uid, page, sort, listId).pipe(take(1))),
      debounceTime(500),
      tap((res: GenFavoritesResponse) => {
        this.router.navigate([], {
          queryParams: {
            'sort': sort,
          },
          queryParamsHandling: 'merge'
        });
        this.favoritesList = res.products;
        this.pagination = res.pagination;
        this.productsCount = res.products.length;

        if (this.productsCount > 0) {
          this.googleAnalyticsService.sendViewItemListEvent(this.favoritesList, this.gaListName);
        }

        this.cd.markForCheck();
      })
    )
  }

  private subscribeToLoadingState(): void {
    this.subscription.add(
      this.genFavoritesService.loading$
        .subscribe(loading => {
          this.loading = loading;
          this.cd.detectChanges();
        })
    );
  }

  private subscribeToContactInfoAndTooltip(): void {
    this.subscription.add(
      combineLatest([
        this.baseStoreService.getTooltipMessage(),
        this.baseStoreService.getPhoneNumber(),
      ]).subscribe(([messsage, phoneOrEmail]) => {
        this.tooltipMessage = messsage?.message;
        this.phoneOrEmail = phoneOrEmail?.contactInfo;
        this.cd.markForCheck();
      })
    );
  }

  private subscribeToActiveListChange(): void {
    this.subscription.add(
      this.activatedRoute.queryParams
        .pipe(
          filter(e => e?.listId?.length > 0),
          tap(e => {
            this.fetchItemsForList(e.listId);
          })
        ).subscribe()
    )
  }

  private openFavoriteProductsAddedToCartDialog(productsAmount: any) {
    const dialog = this.launchDialogService.openDialog(
      GEN_FAVORITES_DIALOGS.FAVORITE_PRODUCTS_ADDED_TO_CART,
      undefined,
      undefined,
      { productsAmount }
    );

    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }
  }

  private setSortOptions(): void {
    this.sorts = favoritesListSortConfig;
  }

  private setSortOptionsUnselected(): void {
    this.sorts = this.sorts.map(item => { return { ...item, selected: false } });
  }

  private setSelectedSortOption(code: string): void {
    const index = this.sorts.findIndex((item: GenFavoritesListSortConfig) => item.code == code);
    this.sorts[index].selected = true;
  }

  public onPageChange(page: number): void {
    this.loading = GenLoadingEnum.Loading;
    this.currentPage = page;
    this.getFavorites(page, this.getSelectedSortOption(), this.currentListId).subscribe(() => {
      this.cd.detectChanges();
    });
  }

  public fetchItemsForList(listId?: string): Subscription {
    let page = 0;
    if (this.currentListId && (listId == this.currentListId)) {
      page = (this.productsCount > 1 || this.currentPage == 0) ? this.currentPage : (this.currentPage - 1);
    }
    this.currentListId = null;
    this.currentListId = listId;
    return this.getFavorites(page, this.getSelectedSortOption(), this.currentListId).pipe(take(1)).subscribe();
  }

  public setActiveList(list: GenFavoritesListItem): void {
    this.currentList = list;
    if (this.currentList.id == this.currentListId) {
      this.fetchItemsForList(this.currentList.id);
    }
  }

  public openDeleteAllItemsFromListModal(list: GenFavoritesListItem): void {
    const modalData = {
      list,
      mode: GenFavotitesDialogActionsEnum.DeleteItems,
      userId: this.userId
    }

    const dialog = this.launchDialogService.openDialog(
      GEN_FAVORITES_DIALOGS.FAVORITES_CONFIRM_ACTION,
      undefined,
      undefined,
      modalData
    );

    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }

    this.subscription.add(
      this.launchDialogService.dialogClose
        .pipe(
          filter((reason) => reason?.mode == GenFavotitesDialogActionsEnum.DeleteItems),
          tap(() => this.genFavoritesService.setFavoritesListChanged(true))
        )
        .subscribe()
    );
  }

  public openAddAllItemsToCartModal(list: GenFavoritesListItem): void {
    const modalData = {
      list,
      mode: GenFavotitesDialogActionsEnum.AddAll,
      userId: this.userId
    }
    const dialog = this.launchDialogService.openDialog(
      GEN_FAVORITES_DIALOGS.FAVORITES_CONFIRM_ACTION,
      undefined,
      undefined,
      modalData
    );

    if (dialog) {
      dialog.pipe(take(1)).subscribe();
    }

    this.subscription.add(
      this.launchDialogService.dialogClose
        .pipe(
          filter(reason => reason?.mode === GenFavotitesDialogActionsEnum.AddAll),
          switchMap(reason =>
            this.activeCartFacade.getActiveCartId().pipe(
              map(cartId => ({ reason, cartId }))
            )
          ),
          tap(({ cartId }) => this.multiCartFacade.reloadCart(cartId)),
          tap(({ reason }) => this.openFavoriteProductsAddedToCartDialog(reason.cartItems))
        )
        .subscribe()
    );
  }

  public getSelectedSortOption(): string {
    const index = this.sorts.findIndex((item: GenFavoritesListSortConfig) => item.selected);
    return this.sorts[index].code;
  }

  public sortList(code: string): void {
    this.setSortOptionsUnselected();
    this.setSelectedSortOption(code);
    this.getFavorites(0, this.getSelectedSortOption(), this.currentListId)
      .subscribe(() => this.cd.markForCheck());
  }

  public trackByFavorites(index: number, item: Product): string {
    return item.code;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
